import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';  // Add this import
import { 
  Plus, 
  Send, 
  File, 
  MoreVertical, 
  FileText, 
  ClipboardList, 
  Loader, 
  Trash2,
  AlertCircle,
  Zap,
  User,
  Settings,
  MessageSquare,
  LogOut,
  CreditCard,
  Star,
  ChevronDown, 
  ChevronRight,
  FileSpreadsheet,
  FileCode,
  FileTextIcon,
  Info,
  Upload,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Files,
  Menu,
  Bot
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { cn } from "../lib/utils";
import UniversalDocumentViewer from './UniversalDocumentViewer';
import DocumentSidebar from './DocumentSidebar';
import ThemeToggle from './ThemeToggle';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ChatService from '../services/chatService';
import MessageComponent from './Message';
import TurboModeToggle from './TurboModeToggle';
import SmartSpreadsheetViewer from './SmartSpreadsheetViewer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "./ui/dialog";
import DataVisualization from './DataVisualization';
import ReactMarkdown from 'react-markdown';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useDocuments } from '../context/DocumentContext';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const LOADING_PHRASES = [
  "Consulting the AI brain trust...",
  "Searching through digital parchment...",
  "Polishing the crystal ball...",
  "Asking the robot overlords...",
  "Brewing some smart coffee...",
  "Waking up the document gnomes...",
  "Calculating 42...",
  "Checking with the data fairies...",
];

const LoadingMessage = ({ message }) => (
  <div className="flex items-center space-x-3 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200/30 dark:border-gray-700/30">
    <div className="relative h-8 w-8 flex items-center justify-center">
      <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-ping" />
      <Bot className="w-5 h-5 text-blue-500 animate-bounce" />
    </div>
    <div className="space-y-2 flex-1">
      <div className="text-sm text-gray-600 dark:text-gray-300 italic">
        {message}
      </div>
      <div className="flex space-x-1">
        {[...Array(3)].map((_, i) => (
          <div 
            key={i}
            className="w-1.5 h-1.5 bg-blue-500/30 rounded-full animate-bounce"
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
    </div>
  </div>
);

const fontClass = "font-inter"; // We'll use this class for consistent font application

const ChatIntroGuide = ({ onExampleClick }) => (
  <div className="flex flex-col items-center justify-center h-full p-6 text-center">
    <div className="max-w-2xl mx-auto">
      <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-blue-100 dark:bg-blue-900/30 rounded-full">
        <Bot className="w-8 h-8 text-blue-600 dark:text-blue-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
        How to Use Document Chat
      </h3>
      
      <div className="grid gap-6 md:grid-cols-2 text-left mb-8">
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-1 text-blue-600 dark:text-blue-400">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">Ask Questions</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                "What's the main conclusion?"<br/>
                "Show me sales data from Q3"
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-1 text-blue-600 dark:text-blue-400">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">Analyze Data</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                "Create a chart of monthly trends"<br/>
                "Compare these two spreadsheets"
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-1 text-blue-600 dark:text-blue-400">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">Quick Tips</h4>
              <ul className="text-sm text-gray-500 dark:text-gray-400 list-disc pl-5 space-y-2">
                <li>Click citations to jump to source</li>
                <li>Use @ to reference specific files</li>
                <li>Shift+Enter for line breaks</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Try asking something like:
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          {[
            "Summarize this document",
            "Show key metrics from the spreadsheet",
            "Compare these two files"
          ].map((example) => (
            <button
              key={example}
              onClick={() => onExampleClick(example)}
              className="px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ChatInterface = ({ 
  documentIds, 
  user, 
  onNewHighlights, 
  onHighlightClick,
  isGeneratingSummary,
  onShowSummary,
  isDocumentViewerCollapsed,
  onToggleDocumentViewer,
  setIsDocumentViewerCollapsed,
  activeTab,
  onTabChange,
  isMobile,
  onToggleSidebar,
  onNewDocument
}) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStage, setLoadingStage] = useState('');
    const [error, setError] = useState(null);
    const [turboMode, setTurboMode] = useState(false);
    const [summary, setSummary] = useState('');
    // Track current document ID for summary management
    const currentDocumentId = documentIds?.[0];
    const messagesEndRef = useRef(null);
    const [loadingMessage, setLoadingMessage] = useState('');

    // Reset summary only when document changes
    useEffect(() => {
      setSummary('');
    }, [currentDocumentId]); // Only reset when the primary document changes

    // Fetch summary when tab is 'summary' and we don't have one
    useEffect(() => {
      const fetchSummary = async () => {
        if (activeTab === 'summary' && !summary && currentDocumentId) {
          try {
            console.log('Fetching summary...');
            const summaryResponse = await onShowSummary();
            console.log('Summary response:', summaryResponse);
            if (summaryResponse?.data?.summary) {
              setSummary(summaryResponse.data.summary);
            } else {
              console.error('Invalid summary response:', summaryResponse);
              setError('Failed to get summary: Invalid response format');
            }
          } catch (error) {
            console.error('Error generating summary:', error);
            setError('Failed to generate summary: ' + (error.message || 'Unknown error'));
          }
        }
      };

      fetchSummary();
    }, [activeTab, currentDocumentId, summary, onShowSummary]);

    // Simplified tab change handler
    const handleTabChange = (tab) => {
      onTabChange(tab);
      
      // Handle document viewer visibility when in mobile mode
      if (isMobile) {
        if (tab === 'view') {
          setIsDocumentViewerCollapsed(false); // Show document viewer
        } else {
          setIsDocumentViewerCollapsed(true); // Hide document viewer
        }
      }
    };

    // Add the missing functions
    const extractCitations = (response) => {
      if (!response.data || !Array.isArray(response.data)) {
        return [];
      }
      return response.data.map(item => ({
        document_id: item.document_id,
        document_name: item.document_name,
        page: item.page_number || 1,
        text: item.text
      }));
    };

    const handleError = (error) => {
      console.error('Error:', error);
      setError(error.message || 'An error occurred');
      setMessages(prev => [...prev, {
        role: 'system',
        content: error.message || 'An error occurred',
        type: 'error',
        timestamp: new Date().toISOString()
      }]);
    };

    useEffect(() => {
      if (documentIds?.length) {
        fetchChatHistory();
      } else {
        setMessages([]);
      }
    }, [documentIds?.join(',')]);
  
    const fetchChatHistory = async () => {
      setIsLoading(true);
      try {
        const history = await ChatService.getChatHistory(documentIds, turboMode);
        
        // Preserve any existing summary messages
        const existingSummaries = messages.filter(msg => msg.type === 'summary');
        const newMessages = history.messages || [];
        
        setMessages([...existingSummaries, ...newMessages]);
      } catch (err) {
        console.error('Error fetching chat history:', err);
        setError('Failed to load chat history');
      } finally {
        setIsLoading(false);
      }
    };
    const handleShowSummary = async () => {
      if (!documentIds?.length) return;
      
      try {
        const response = await onShowSummary();
        
        setMessages(prev => [
          ...prev,
          {
            id: uuidv4(),
            role: 'system',
            content: 'Here\'s a summary of the document:',
            type: 'summary',
            className: "text-gray-900 dark:text-gray-100",
            timestamp: new Date().toISOString()
          },
          {
            id: uuidv4(),
            role: 'assistant',
            content: response.data.summary,
            type: 'summary',
            className: "text-gray-800 dark:text-gray-200",
            timestamp: new Date().toISOString()
          }
        ]);
      } catch (error) {
        console.error('Error showing summary:', error);
        setMessages(prev => [...prev, {
          id: uuidv4(),
          role: 'system',
          content: 'Sorry, there was an error retrieving the summary.',
          type: 'error',
          timestamp: new Date().toISOString()
        }]);
      }
    };
    const handleSendMessage = async (content) => {
      const messageContent = String(content || '').trim();
      if (!messageContent || !documentIds?.length) return;

      const userMessage = { role: 'user', content: messageContent };
      setMessages(prev => [...prev, userMessage]);
      
      // Set random loading message
      setLoadingMessage(LOADING_PHRASES[Math.floor(Math.random() * LOADING_PHRASES.length)]);
      
      try {
        setIsLoading(true);
        const response = await ChatService.sendMessage({
          document_ids: documentIds,
          content: messageContent,
          use_turbo: turboMode
        });
        
        const newMessage = {
          role: 'assistant',
          content: response.message?.content,
          citations: response.message?.citations || [],
          visualization: response.message?.visualization,
          table_data: response.message.table_data,
          timestamp: new Date().toISOString(),
          used_turbo: turboMode
        };
        
        setMessages(prev => [...prev, newMessage]);
      } catch (error) {
        console.error('Error sending message:', error);
        handleError(error);
      } finally {
        setIsLoading(false);
        setLoadingMessage('');
      }
    };
    const handleTurboToggle = () => {
      console.log('Toggling turbo mode:', !turboMode); // Debug log
      setTurboMode(!turboMode);
    };
    const handleCitationClick = (citation) => {
      console.log('Citation clicked:', citation);
      
      if (!citation?.text) {
        console.warn('Invalid citation data');
        return;
      }

      const rawPageNumber = citation.page || citation.page_number;
      if (!rawPageNumber) {
        console.warn('No page number found in citation:', citation);
        return;
      }

      // Create highlight data with zero-based page index
      const highlightData = {
        documentId: citation.document_id || citation.documentId,
        page: parseInt(rawPageNumber, 10) - 1, // Convert to zero-based index
        text: citation.text.trim()
      };

      console.log('ChatInterface: Calling onHighlightClick with:', highlightData);
      onHighlightClick(highlightData);
    };
  
    // Add scroll effect
    const scrollToBottom = useCallback(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
      scrollToBottom();
    }, [messages, loadingMessage]);

    useEffect(() => {
      console.log('Documents updated:', messages);
    }, [messages]);

    return (
      <div className="flex flex-col h-full">
        {/* Always show header with tabs */}
        <div className="flex items-center h-12 px-4 bg-gray-100 dark:bg-gray-800/50">
          <div className="flex flex-1 space-x-2">
            <button 
              onClick={() => handleTabChange('chat')}
              className={cn(
                "flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center space-x-2",
                activeTab === 'chat' 
                  ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 shadow-inner" 
                  : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              )}
            >
              <MessageSquare className="w-4 h-4" />
              {!isMobile && <span>Chat</span>}
            </button>
            
            <button 
              onClick={() => handleTabChange('summary')}
              className={cn(
                "flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center space-x-2",
                activeTab === 'summary'
                  ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 shadow-inner"
                  : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              )}
            >
              <ClipboardList className="w-4 h-4" />
              {!isMobile && <span>Summary</span>}
            </button>

            {isMobile && (
              <button 
                onClick={() => handleTabChange('view')}
                className={cn(
                  "flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center space-x-2",
                  activeTab === 'view'
                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 shadow-inner"
                    : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                )}
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span>View</span>
              </button>
            )}
          </div>
        </div>

        {/* Chat messages area */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'chat' ? (
            !documentIds?.length ? (
              <ChatIntroGuide onExampleClick={(text) => setInputMessage(text)} />
            ) : isLoading && !messages.length ? (
              <div className="flex justify-center items-center h-full p-4">
                <Loader className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : messages.length === 0 ? (
              <ChatIntroGuide onExampleClick={(text) => setInputMessage(text)} />
            ) : (
              <div className="space-y-0.5">
                {messages.map((msg, index) => (
                  <div 
                    key={index}
                    className={cn(
                      "py-4 animate-in fade-in-0 slide-in-from-bottom-2",
                      msg.role === 'user' ? 'bg-transparent px-4' : 'bg-gray-50/30 dark:bg-gray-900/50 px-4',
                      msg.role === 'assistant' && "border-t border-b border-gray-100/50 dark:border-gray-700/30"
                    )}
                  >
                    <div className={cn(
                      "max-w-screen-lg mx-auto",
                      msg.role === 'user' ? 'space-y-2' : 'space-y-4'
                    )}>
                      <MessageComponent
                        message={msg}
                        usedTurbo={msg.used_turbo}
                        onCitationClick={handleCitationClick}
                      />
                    </div>
                  </div>
                ))}
                {loadingMessage && (
                  <div className="py-4 px-4">
                    <div className="max-w-screen-lg mx-auto">
                      <LoadingMessage message={loadingMessage} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )
          ) : (
            // Summary content with improved dark mode styling
            <div className="space-y-6 p-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <h2 className="flex items-center text-xl font-semibold mb-4">
                  {isGeneratingSummary ? (
                    <Loader className="w-5 h-5 mr-2 animate-spin text-blue-500" />
                  ) : (
                    <ClipboardList className="w-5 h-5 mr-2 text-blue-500" />
                  )}
                  <span className="text-gray-900 dark:text-gray-100">
                    {isGeneratingSummary ? (
                      <span className="flex items-center">
                        <Loader className="w-4 h-4 mr-2 animate-spin text-current" />
                        Generating Summary...
                      </span>
                    ) : 'Document Summary'}
                  </span>
                </h2>
                <div className="prose prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-200">
                  {summary ? (
                    <ReactMarkdown
                      components={{
                        h2: ({node, ...props}) => <h2 className="text-lg font-semibold mt-4 mb-2" {...props} />,
                        p: ({node, ...props}) => <p className="my-3" {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc pl-6 my-3" {...props} />,
                        li: ({node, ...props}) => <li className="my-1" {...props} />
                      }}
                    >
                      {summary}
                    </ReactMarkdown>
                  ) : (
                    <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
                      {isGeneratingSummary ? (
                        <Loader className="w-6 h-6 animate-spin text-blue-500" />
                      ) : (
                        'No summary available'
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Message input area */}
        {activeTab === 'chat' && (
          <div className="flex-shrink-0 border-t p-4">
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage(inputMessage);
                setInputMessage('');
              }}
              className="flex-shrink-0 border-t border-gray-100 dark:border-gray-800/50 bg-gradient-to-b from-white/80 to-white/30 dark:from-gray-900/50 dark:to-gray-900/30 backdrop-blur-lg p-4"
            >
              <div className="flex items-center space-x-2 relative">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Ask anything about the document..."
                  className={`flex-1 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50
                    bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm
                    border border-gray-200/50 dark:border-gray-700/50
                    text-gray-900 dark:text-white
                    shadow-sm hover:shadow transition-all duration-300
                    ${fontClass} text-[15px]`}
                />
                <button
                  type="submit"
                  disabled={isLoading || !documentIds?.length || !inputMessage.trim()}
                  className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl 
                    hover:from-blue-700 hover:to-blue-600 shadow-lg hover:shadow-xl
                    transition-all duration-300 transform hover:scale-[1.02]"
                >
                  {isLoading ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  };

const DocumentChatInterface = ({ user, onSignOut, onNewDocument }) => {
  const WS_URL = process.env.REACT_APP_WS_URL || 'wss://predica.ai/ws';
  const { 
    documents, 
    setDocuments, 
    fetchDocuments,
    processingDocuments,
    setProcessingDocuments 
  } = useDocuments();
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeDocuments, setActiveDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [isLoadingFolders, setIsLoadingFolders] = useState(true);
  const [documentSets, setDocumentSets] = useState([]);
  const [selectedDocumentSet, setSelectedDocumentSet] = useState(null);
  // Mobile and UI states
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState('chat');
  // const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // const [isDocumentViewerCollapsed, setIsDocumentViewerCollapsed] = useState(false);
  // Upload states
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Chat states
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    { role: 'system', content: 'How can I help you with your document?' },
  ]);
  const [highlights, setHighlights] = useState([]);

  // UI states
  const [error, setError] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [folders, setFolders] = useState([]);
  const navigate = useNavigate();
  const [activeHighlight, setActiveHighlight] = useState(null);
  const [activeDocument, setActiveDocument] = useState(null);

  // ... other state declarations ...
  const handleStartMultiChat = (selectedDocs) => {
    // Create a new document set
    const newSet = {
      id: uuidv4(),
      name: `${getFileTypeName(selectedDocs[0].file_type)} Set (${selectedDocs.length})`,
      documents: selectedDocs,
      created_at: new Date().toISOString()
    };

    // Add to document sets
    setDocumentSets(prev => [...prev, newSet]);
    
    // Set as active set
    setSelectedDocumentSet(newSet);
    setActiveDocument(selectedDocs[0]);
    setActiveDocuments(selectedDocs);
    
    // Reset messages for new chat
    setMessages([]);
    setHighlights([]);
  };
  const handleNewDocument = () => {
    setUploadDialogOpen(true);
  };
  useEffect(() => {
    fetchFolders();
  }, []);

  // const { processingDocuments } = useWebSocket();
  // Fetch documents on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingDocuments(true);
        setIsLoadingFolders(true);
        setError(null);

        // Fetch folders
        const foldersResponse = await axios.get(`${API_URL}/folders`, {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`
          }
        });

        // Fetch documents
        const documentsResponse = await axios.get(`${API_URL}/documents/list?cache=${Date.now()}`, {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`
          }
        });

        console.log('Folders response:', foldersResponse.data);
        console.log('Documents response:', documentsResponse.data);

        if (foldersResponse.data) {
          setFolders(foldersResponse.data);
        }

        if (documentsResponse.data?.documents) {
          const docs = documentsResponse.data.documents || [];
          setDocuments(docs);
          if (!selectedDocument && docs.length > 0) {
            setSelectedDocument(docs[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setIsLoadingDocuments(false);
        setIsLoadingFolders(false);
      }
    };

    fetchData();
  }, [user]);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/folders`, {
        headers: {
          'Authorization': `Bearer ${user?.access_token}`
        }
      });
      
      if (response.data) {
        setFolders(response.data);
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError('Failed to load folders');
    }
  }, [user?.access_token]);

  const handleCreateFolder = useCallback(async (name) => {
    try {
      const response = await axios.post(
        `${API_URL}/folders`,
        { name },
        {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        setFolders(prev => [...prev, response.data]);
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      throw new Error('Failed to create folder');
    }
  }, [user?.access_token]);

  const handleDeleteFolder = useCallback(async (folderId) => {
    try {
      await axios.delete(`${API_URL}/folders/${folderId}`, {
        headers: {
          'Authorization': `Bearer ${user?.access_token}`
        }
      });

      setFolders(prev => prev.filter(folder => folder.id !== folderId));
      setDocuments(prev => prev.map(doc => 
        doc.folder_id === folderId ? { ...doc, folder_id: null } : doc
      ));
    } catch (err) {
      console.error('Error deleting folder:', err);
      throw new Error('Failed to delete folder');
    }
  }, [user?.access_token]);
  const handleMoveDocument = useCallback(async (documentId, folderId) => {
    try {
      const response = await axios.patch(
        `${API_URL}/documents/${documentId}/move`,
        { folderId: folderId === 'root' ? null : folderId },
        {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        setDocuments(prev => prev.map(doc => 
          doc.id === documentId 
            ? { ...doc, folder_id: folderId === 'root' ? null : folderId }
            : doc
        ));
      }
    } catch (err) {
      console.error('Error moving document:', err);
      setError('Failed to move document');
    }
  }, [user?.access_token]);

  useEffect(() => {
    console.log('Documents updated:', documents);
    console.log('Current folders state:', folders);
  }, [documents, folders]);

  const handleDocumentSelect = useCallback((document) => {
    setSelectedDocument(document);
    setSelectedDocumentSet(null);
    setActiveDocument(document);
    setActiveDocuments([document]);
    setMessages([]);
    setHighlights([]);
  }, []);
  const handleDocumentSetSelect = useCallback((set) => {
    setSelectedDocumentSet(set);
    setSelectedDocument(null);
    setActiveDocument(set.documents[0]);
    setActiveDocuments(set.documents);
    setMessages([]);
    setHighlights([]);
  }, []);

  const handleDeleteClick = (doc, e) => {
    e.stopPropagation();
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDocument = async (documentOrId) => {
    try {
      const documentId = typeof documentOrId === 'string' ? documentOrId : documentOrId.id;
      
      await axios.delete(`${API_URL}/documents/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${user?.access_token}`
        }
      });

      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      if (selectedDocument?.id === documentId) {
        setSelectedDocument(null);
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
      throw err; // Propagate error to handle it in the sidebar
    }
  };

  // Group documents by folder
  const documentsByFolder = useMemo(() => {
    return (documents || []).reduce((acc, doc) => {
      const folderId = doc.folder_id || 'root';
      if (!acc[folderId]) {
        acc[folderId] = [];
      }
      acc[folderId].push(doc);
      return acc;
    }, {});
  }, [documents]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!documentToDelete) return;
    
    setIsDeleting(true);
    try {
      await handleDeleteDocument(documentToDelete);

      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  }, [documentToDelete, handleDeleteDocument]);

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    const newMessage = { role: 'user', content: message };
    setMessages(prev => [...prev, newMessage]);
    setMessage('');

    try {
      const aiResponse = {
        role: 'assistant',
        content: 'Based on the document, I can help explain that. Would you like me to elaborate?',
        citations: [{
          id: uuidv4(),
          text: "relevant text",
          page: 1
        }]
      };

      setMessages(prev => [...prev, aiResponse]);

      if (aiResponse.citations) {
        setHighlights(prev => [
          ...prev,
          ...aiResponse.citations.filter(citation => 
            !prev.some(h => h.id === citation.id)
          )
        ]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Sorry, there was an error processing your message.',
        type: 'error'
      }]);
    }
  }, [message]);

  const handleCreateDocumentSet = useCallback((documentIds) => {
    const selectedDocs = documents.filter(doc => documentIds.includes(doc.id));
    const newSet = {
      id: uuidv4(),
      documents: selectedDocs,
      created_at: new Date().toISOString()
    };
    setDocumentSets(prev => [...prev, newSet]);
    setSelectedDocumentSet(newSet);
    setSelectedDocument(null);
  }, [documents]);

  const handleDeleteDocumentSet = useCallback((setId) => {
    setDocumentSets(prev => prev.filter(set => set.id !== setId));
    if (selectedDocumentSet?.id === setId) {
      setSelectedDocumentSet(null);
    }
  }, [selectedDocumentSet?.id]);

  const handleRenameDocumentSet = useCallback((setId, newName) => {
    setDocumentSets(prev => prev.map(set => 
      set.id === setId 
        ? { ...set, name: newName }
        : set
    ));
  }, []);

  const handleGenerateReport = useCallback(async () => {
    if (!selectedDocument) return;
  
    setIsGeneratingReport(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const qaHistory = messages.filter(msg => 
        msg.role === 'user' || (msg.role === 'assistant' && !msg.type)
      );
      
      setMessages(prev => [
        ...prev,
        {
          role: 'system',
          content: 'Here\'s a report based on your questions and the document:',
          type: 'report'
        },
        {
          role: 'assistant',
          content: `Document Analysis Report\n\nQuestions Asked:\n${
            qaHistory.filter(msg => msg.role === 'user')
              .map(msg => `- ${msg.content}`)
              .join('\n')
          }\n\nKey Insights:\n1. Based on your questions...\n2. The analysis shows...\n3. Regarding your inquiry...`,
          type: 'report'
        }
      ]);
    } catch (error) {
      console.error('Error generating report:', error);
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Sorry, there was an error generating the report.',
        type: 'error'
      }]);
    } finally {
      setIsGeneratingReport(false);
    }
  }, [selectedDocument, messages]);

  const handleHighlightClick = useCallback((citation) => {
    if (!citation?.text) return;
    
    const highlightData = {
      documentId: citation.document_id || citation.documentId,
      page: parseInt(citation.page || citation.page_number || 1, 10),
      text: citation.text.trim()
    };

    setActiveHighlight(highlightData);
  }, []);

  const [showSummary, setShowSummary] = useState(false);

  const fetchDocumentSummary = useCallback(async (documentId) => {
    try {
      const response = await axios.get(`${API_URL}/documents/${documentId}/summary`, {
        headers: {
          'Authorization': `Bearer ${user?.access_token}`
        }
      });
      return response.data.summary;
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    }
  }, [user?.access_token]);

  const handleShowSummary = async () => {
    if (!selectedDocument) {
      throw new Error('No document selected');
    }
    
    try {
      setIsGeneratingSummary(true);
      const response = await axios.get(
        `${API_URL}/documents/${selectedDocument.id}/summary`,
        {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`
          }
        }
      );
      
      if (!response.data?.summary) {
        throw new Error('Invalid summary response');
      }
      
      return response;
    } catch (error) {
      console.error('Error getting summary:', error);
      throw error;
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleSpreadsheetQuery = (query) => {
    // Add the query to the chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: query
    }]);
    // Process the query
    handleSendMessage(query);
  };

  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  const FeedbackDialog = ({ open, onOpenChange }) => {
    const [feedback, setFeedback] = useState('');
    const [rating, setRating] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
      if (!feedback.trim()) return;
      
      setIsSubmitting(true);
      try {
        await axios.post(`${API_URL}/feedback`, {
          feedback,
          rating,
          user_id: user.id
        }, {
          headers: {
            'Authorization': `Bearer ${user.access_token}`
          }
        });
        
        onOpenChange(false);
        // You might want to show a success toast here
      } catch (error) {
        console.error('Error submitting feedback:', error);
        // Show error message
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Submit Feedback</DialogTitle>
            <DialogDescription>
              Help us improve by sharing your experience with Predica.AI.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Rating */}
            <div className="flex items-center justify-center space-x-1">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  onClick={() => setRating(value)}
                  className={`p-1 rounded-full transition-colors ${
                    value <= rating 
                      ? 'text-yellow-400 hover:text-yellow-500' 
                      : 'text-gray-300 hover:text-gray-400'
                  }`}
                >
                  <Star className="w-8 h-8 fill-current" />
                </button>
              ))}
            </div>
            
            {/* Feedback Text */}
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell us what you think..."
              className="w-full h-32 px-3 py-2 text-gray-900 dark:text-white 
                placeholder-gray-400 border border-gray-300 dark:border-gray-600 
                rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
                bg-white dark:bg-gray-800"
            />
          </div>

          <DialogFooter>
            <button
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600
                rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !feedback.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Add this helper function to get the appropriate icon for each file type
  const getFileIcon = useCallback((fileType) => {
    switch (true) {
      case fileType.includes('spreadsheetml.sheet'):
      case fileType.includes('ms-excel'):
      case fileType.includes('csv'):
        return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
      case fileType.includes('pdf'):
        return <File className="w-5 h-5 text-red-500" />;
      case fileType.includes('text'):
        return <FileTextIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <FileCode className="w-5 h-5 text-gray-500" />;
    }
  }, []);

  const handleUploadComplete = (uploadedFiles) => {
    // Remove completed files from uploading list
    setUploadingFiles(prev => prev.filter(f => 
      !uploadedFiles.some(uf => uf.name === f.name)
    ));
    fetchDocuments(); // Refresh the documents list
  };

  const handleUploadStart = (files) => {
    setUploadingFiles(files);
    // const { setProcessingDocuments } = useWebSocket();
    
    // Optimistically add to processing state
    files.forEach(file => {
      const tempId = `upload-${file.name}-${Date.now()}`;
      setProcessingDocuments(prev => ({
        ...prev,
        [tempId]: {
          progress: 0,
          status: 'processing',
          filename: file.name,
          file_type: file.type
        }
      }));
    });
  };

  const UploadDialog = ({ open, onOpenChange, onUploadComplete, user }) => {
    const [files, setFiles] = useState([]);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const fileInputRef = useRef(null);

    const handleDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFiles(Array.from(e.dataTransfer.files));
      }
    };

    const handleChange = (e) => {
      if (e.target.files && e.target.files[0]) {
        handleFiles(Array.from(e.target.files));
      }
    };

    const handleFiles = (newFiles) => {
      setError(null);
      const validFiles = newFiles.filter(file => validateFile(file));
      setFiles(prevFiles => [...prevFiles, ...validFiles]);
    };

    const validateFile = (file) => {
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain'
      ];
      
      if (!validTypes.includes(file.type)) {
        setError(`File type not supported: ${file.name}`);
        return false;
      }
      return true;
    };

    const removeFile = (fileToRemove) => {
      setFiles(prevFiles => prevFiles.filter(f => f !== fileToRemove));
    };

    const handleUpload = async () => {
      if (files.length === 0) {
        setError('Please select files to upload');
        return;
      }

      setIsUploading(true);
      setError(null);

      // Add files to parent's uploading files list
      setUploadingFiles(prev => [...prev, ...files]);
      
      // Close the dialog immediately
      onOpenChange(false);

      try {
        const formData = new FormData();
        files.forEach(file => {
          formData.append('files', file);
        });

        const response = await axios.post(
          `${API_URL}/documents/upload`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${user?.access_token}`,
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(prev => ({
                ...prev,
                total: percentCompleted,
                ...files.reduce((acc, file) => ({
                  ...acc,
                  [file.name]: percentCompleted
                }), {})
              }));
            },
          }
        );

        if (response.data?.files) {
          onUploadComplete(files);
        } else {
          throw new Error('Invalid response format');
        }

      } catch (error) {
        console.error('Upload error:', error);
        setError(
          error.response?.data?.detail || 
          error.message || 
          'Failed to upload documents'
        );
        // Remove files from uploading list on error
        setUploadingFiles(prev => prev.filter(f => 
          !files.some(uf => uf.name === f.name)
        ));
      } finally {
        setIsUploading(false);
        setFiles([]); // Clear files after upload attempt
      }
    };

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className={`text-xl ${fontClass}`}>Upload Documents</DialogTitle>
            <DialogDescription className={`${fontClass} text-[15px] text-gray-600 dark:text-gray-300`}>
              Upload your documents to start chatting with them
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="p-4 rounded-xl bg-red-50/50 dark:bg-red-900/20 backdrop-blur-xl
              border border-red-100 dark:border-red-800/50 text-red-700 dark:text-red-300">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
            className="relative rounded-xl p-8 text-center cursor-pointer
              transition-all duration-300 transform hover:scale-[1.02]
              bg-white/70 dark:bg-gray-900/40 backdrop-blur-xl
              border-2 border-dashed border-gray-300/50 dark:border-gray-600/50 
              hover:border-gray-400 dark:hover:border-gray-500"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleChange}
              accept=".pdf,.docx,.txt,.xlsx,.xls,.csv"
              className="hidden"
            />
            <Upload className="mx-auto h-12 w-12 text-blue-500/80" />
            <p className="mt-4 text-base font-medium text-gray-700 dark:text-gray-200">
              Drag 'n' drop some files here, or click to select files
            </p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Supported formats: PDF, DOCX, TXT, XLSX, XLS, CSV
            </p>
          </div>

          {/* Add back the files list */}
          {files.length > 0 && (
            <div className="space-y-3 max-h-[200px] overflow-y-auto">
              {files.map((file, index) => (
                <div key={index} className="flex items-center space-x-4 p-3 rounded-xl
                  bg-white/70 dark:bg-gray-900/40 backdrop-blur-xl
                  border border-gray-200/20 dark:border-gray-700/20">
                  <div className="flex-shrink-0">
                    {getFileIcon(file.type)}
                  </div>
                  <div className="flex-grow min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file);
                    }}
                    disabled={isUploading}
                    className="p-1.5 rounded-full text-red-500 hover:text-red-700 dark:text-red-400
                      hover:bg-red-50 dark:hover:bg-red-900/20
                      disabled:opacity-50 disabled:cursor-not-allowed
                      transition-all duration-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <button
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200
                hover:bg-gray-100 dark:hover:bg-gray-800
                transition-all duration-300"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={isUploading || files.length === 0}
              className="px-4 py-2 rounded-lg font-medium text-white
                bg-gradient-to-r from-blue-600 to-blue-500 
                shadow-lg hover:shadow-xl
                transition-all duration-300 transform hover:scale-105
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center space-x-2"
            >
              {isUploading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload Files</span>
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDocumentViewerCollapsed, setIsDocumentViewerCollapsed] = useState(false);

  const getFileTypeName = (fileType) => {
    switch (true) {
      case fileType.includes('pdf'):
        return 'PDF';
      case fileType.includes('spreadsheetml.sheet'):
      case fileType.includes('ms-excel'):
      case fileType.includes('csv'):
        return 'Spreadsheet';
      case fileType.includes('text'):
        return 'Text';
      default:
        return 'Document';
    }
  };

  // Add resize listener to the main component
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Add document viewer toggle handler
  const handleToggleDocumentViewer = () => {
    setIsDocumentViewerCollapsed(!isDocumentViewerCollapsed);
  };

  // Add handler for sidebar toggle
  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    console.log('Documents updated:', documents);
  }, [documents]);

  return (
    <div className={`flex h-screen overflow-hidden bg-[#fbfbfd] dark:bg-[#0d1117] relative ${fontClass}`}>
      {/* Background Effects - Dark Mode */}
      <div className="absolute inset-0 dark:block hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/5 rounded-full blur-[120px]" />
        
        {/* Subtle Grid */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-800/[0.02] to-transparent 
          [mask-image:linear-gradient(to_bottom,transparent,white,transparent)]" 
          style={{ 
            backgroundSize: '50px 50px',
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)`
          }} 
        />
      </div>

      {/* Left Sidebar - Hide on mobile when collapsed */}
      <div className={cn(
        "transition-all duration-300 ease-in-out",
        "border-r border-gray-200/30 dark:border-gray-800/30",
        "bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl",
        "shadow-sm",
        isSidebarCollapsed ? "w-0" : "w-72",
        "md:static fixed inset-y-0 left-0",
        "md:z-0 z-[60]",
        isSidebarCollapsed ? "-translate-x-full md:translate-x-0" : "translate-x-0"
      )}>
        <div className={cn(
          "h-full",
          isSidebarCollapsed ? "hidden" : "block"
        )}>
          <DocumentSidebar
            documents={documents}
            folders={folders}
            fetchDocuments={fetchDocuments}
            selectedDocument={selectedDocument}
            selectedDocumentSet={selectedDocumentSet}
            documentSets={documentSets}
            onRenameDocumentSet={handleRenameDocumentSet}
            onDocumentSelect={handleDocumentSelect}
            onDocumentSetSelect={handleDocumentSetSelect}
            onCreateDocumentSet={handleCreateDocumentSet}
            onDeleteDocumentSet={handleDeleteDocumentSet}
            onDeleteDocument={handleDeleteDocument}
            onNewDocument={handleNewDocument}
            onStartMultiChat={handleStartMultiChat}
            user={user}
            onCreateFolder={handleCreateFolder}
            onDeleteFolder={handleDeleteFolder}
            onMoveDocument={handleMoveDocument}
            isLoading={isLoadingDocuments}
            error={error}
            processingDocuments={processingDocuments}
            activeHighlight={activeHighlight}
          />
        </div>
      </div>

      {/* Add a backdrop overlay for mobile */}
      {isMobile && !isSidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      {/* Main Content Area - Updated styling */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Simplified Header */}
        <header className={cn(
          "flex-shrink-0 flex h-14 items-center px-4 gap-4",
          "bg-transparent",
          "border-b border-gray-200/20 dark:border-gray-800/20"
        )}>
          {isMobile ? (
            <>
              <button
                onClick={handleToggleSidebar}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 
                  dark:hover:text-white transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              <span className={`font-medium text-gray-900 dark:text-white ${fontClass} text-[15px]`}>
                predica.ai
              </span>
            </>
          ) : (
            <button
              onClick={handleToggleSidebar}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 
                dark:hover:text-white transition-colors"
            >
              {isSidebarCollapsed ? (
                <PanelLeftOpen className="w-5 h-5" />
              ) : (
                <PanelLeftClose className="w-5 h-5" />
              )}
            </button>
          )}

          {/* Center section with document title/info */}
          <div className="flex-1 flex items-center justify-center">
            {activeDocument && (
              <div className="flex items-center space-x-2 text-sm">
                {getFileIcon(activeDocument.file_type)}
                <span className={`font-medium text-gray-700 dark:text-gray-200 ${fontClass} text-[15px]`}>
                  {activeDocument.filename}
                </span>
              </div>
            )}
          </div>

          {/* Right section with controls */}
          <div className="flex items-center space-x-2">
            {!isMobile && (
              <button
                onClick={handleToggleDocumentViewer}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 
                  dark:hover:text-white transition-colors"
              >
                {isDocumentViewerCollapsed ? (
                  <PanelRightOpen className="w-5 h-5" />
                ) : (
                  <PanelRightClose className="w-5 h-5" />
                )}
              </button>
            )}
            <ThemeToggle />
            <UserButton 
              user={user} 
              onSignOut={onSignOut}
              navigate={navigate}
              setShowFeedbackDialog={setShowFeedbackDialog}
            />
          </div>
        </header>

        {/* Main Content Grid - Updated for better spacing */}
        <div className={cn(
          "flex-1 flex min-h-0",
          isMobile ? "flex-col" : "p-4 gap-3"
        )}>
          {/* Document Viewer Panel - Keep header visible */}
          <div className={cn(
            "transition-all duration-300 rounded-lg overflow-hidden",
            "bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl",
            "border border-gray-200/30 dark:border-gray-800/30",
            "shadow-sm",
            isMobile ? (
              activeTab === 'view' ? "flex-1 flex flex-col" : "hidden"
            ) : (
              isDocumentViewerCollapsed ? "w-0" : "w-1/2"
            )
          )}>
            {(!isDocumentViewerCollapsed || (isMobile && activeTab === 'view')) && (
              <>
                {/* Content area */}
                <div className="flex-1 h-full overflow-hidden">
                  {!activeDocument ? (
                    <div className="flex flex-col items-center justify-center h-full p-4">
                      <div className="relative w-48 h-48 mb-6">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full animate-pulse" />
                        <div className="flex items-center justify-center w-full h-full">
                          <FileText className="w-32 h-32 text-gray-300 dark:text-gray-600 animate-float" />
                        </div>
                      </div>
                      <p className="text-center text-gray-500 dark:text-gray-400 mb-4 text-lg">
                        Start by uploading a document or selecting one from your library
                      </p>
                    </div>
                  ) : (
                    <UniversalDocumentViewer
                      documentUrl={activeDocument ? (
                        activeDocument.s3_url || 
                        `${API_URL}/documents/stream/${activeDocument.id}`
                      ) : null}
                      documentType={activeDocument?.file_type}
                      documentId={activeDocument?.id}
                      documents={selectedDocumentSet ? activeDocuments : null}
                      user={user}
                      highlights={highlights}
                      activeHighlight={activeHighlight}
                      onAskAboutData={handleSpreadsheetQuery}
                      onDocumentSwitch={(doc) => {
                        setActiveDocument(doc);
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Chat Interface Panel */}
          <div className={cn(
            "transition-all duration-300 flex flex-col min-h-0 rounded-lg overflow-hidden",
            "bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl",
            "border border-gray-200/30 dark:border-gray-800/30",
            "shadow-sm",
            isMobile ? (
              activeTab === 'chat' || activeTab === 'summary' ? "flex-1" : "hidden"
            ) : (
              isDocumentViewerCollapsed ? "flex-1" : "w-1/2"
            )
          )}>
            <ChatInterface
              documentIds={activeDocuments.map(doc => doc.id)}
              user={user}
              onNewHighlights={setHighlights}
              onHighlightClick={handleHighlightClick}
              isGeneratingSummary={isGeneratingSummary}
              onShowSummary={handleShowSummary}
              isDocumentViewerCollapsed={isDocumentViewerCollapsed}
              setIsDocumentViewerCollapsed={setIsDocumentViewerCollapsed}
              onToggleDocumentViewer={handleToggleDocumentViewer}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              isMobile={isMobile}
              onToggleSidebar={handleToggleSidebar}
              onNewDocument={handleNewDocument}
            />
          </div>
        </div>
      </div>

      {/* Update the AlertDialog styling */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-200/20 dark:border-gray-700/20">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{documentToDelete?.filename}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add FeedbackDialog */}
      <FeedbackDialog 
        open={showFeedbackDialog} 
        onOpenChange={setShowFeedbackDialog}
      />

      <UploadDialog 
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onUploadComplete={handleUploadComplete}
        user={user}
      />

      {/* Mobile document toggle - Updated styling */}
      {isMobile && (
        <button
          onClick={handleToggleDocumentViewer}
          className="fixed bottom-20 right-4 z-50 p-2.5 rounded-lg
            bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl
            border border-gray-200/30 dark:border-gray-800/30
            shadow-sm hover:shadow
            text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white
            transition-all duration-300"
        >
          <Files className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

// Helper component for user button
const UserButton = memo(({ user, onSignOut, navigate, setShowFeedbackDialog }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="relative rounded-full overflow-hidden 
        hover:ring-2 hover:ring-blue-500/20 dark:hover:ring-blue-500/20 
        transition-all duration-300 transform hover:scale-105
        shadow-lg hover:shadow-xl">
        {user?.imageUrl ? (
          <img 
            src={user.imageUrl} 
            alt={user.name || 'User profile'} 
            className="w-8 h-8 rounded-full cursor-pointer"
          />
        ) : (
          <div className="w-8 h-8 rounded-full 
            bg-gradient-to-br from-blue-500/10 to-purple-500/10 
            dark:from-blue-500/20 dark:to-purple-500/20
            flex items-center justify-center">
            <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>
        )}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent 
      align="end" 
      className="w-56 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl 
        border border-gray-200/20 dark:border-gray-700/20 shadow-xl"
    >
      {/* User Info */}
      <div className="px-2 py-1.5 border-b border-gray-200 dark:border-gray-700">
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          {user?.name || 'User'}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {user?.email}
        </div>
      </div>
      
      {/* Menu Items */}
      <DropdownMenuItem 
        className="flex items-center cursor-pointer"
        onClick={() => window.location.href = 'https://billing.stripe.com/p/login/4gw4gi41z9uneYg000'}
      >
        <Settings className="w-4 h-4 mr-2" />
        Manage Subscription
      </DropdownMenuItem>
      
      <DropdownMenuItem 
        className="flex items-center cursor-pointer"
        onClick={() => setShowFeedbackDialog(true)}
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        Submit Feedback
      </DropdownMenuItem>

      <div className="h-px bg-gray-200 dark:bg-gray-700 my-1" />
      
      <DropdownMenuItem 
        className="flex items-center cursor-pointer text-red-600 dark:text-red-400"
        onClick={onSignOut}
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
));

export default DocumentChatInterface;